import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { applyBranch10Promotion } from "@/components/promotions/branch10promotion";
import { CartItem } from "@/types/cart";
import { Branch } from "@/types/branch";

interface UseCartCalculationsProps {
  branch: Branch | undefined;
  tipAmount: number;
  discountValue: number;
  deliveryFee: string;
  usePoints: boolean;
  points: string;
}

export function useCartCalculations({
  branch,
  tipAmount,
  discountValue,
  deliveryFee,
  usePoints,
  points,
}: UseCartCalculationsProps) {
  const cart = useSelector((state: RootState) => state.cart.items);
  const branchId = useSelector((state: RootState) => state.cart.branchId);
  const serviceFee =
    useSelector((state: RootState) => state.cart.serviceFee) || 0;
  const taxRate = useSelector((state: RootState) => state.cart.taxRate) || 0;

  const calculateItemTotal = useCallback((item: CartItem) => {
    const baseTotal = parseFloat(item.unit_price ?? "0.00") * item.quantity;
    const optionsTotal = Object.values(item.selected_options).reduce(
      (total, option) => {
        if (option.selected && typeof option.surcharge === "number") {
          return (
            total + (option.surcharge ?? 0) * option.quantity * item.quantity
          );
        }
        return total;
      },
      0,
    );
    return (baseTotal + optionsTotal).toFixed(2);
  }, []);

  const productPrice = useCallback(
    (item: CartItem) => {
      return (parseFloat(calculateItemTotal(item)) / item.quantity).toFixed(2);
    },
    [calculateItemTotal],
  );

  const adjustedCart = useMemo(() => {
    if (branchId === "10") {
      return applyBranch10Promotion(cart);
    }
    return cart;
  }, [cart, branchId]);

  const subtotal = useMemo(() => {
    return adjustedCart.reduce(
      (total, item) => total + parseFloat(calculateItemTotal(item) || "0"),
      0,
    );
  }, [adjustedCart, calculateItemTotal]);

  const tax = useMemo(() => {
    return subtotal * (taxRate / 100);
  }, [subtotal, taxRate]);

  const pointsToRedeem = useMemo(() => {
    const deliveryFeeValue =
      deliveryFee.length > 0 ? parseFloat(deliveryFee) : 0;
    return Math.min(
      parseFloat(branch?.max_points_to_redeem || "100000"),
      parseFloat(points),
      (subtotal +
        tax +
        serviceFee +
        tipAmount -
        discountValue +
        deliveryFeeValue) /
        parseFloat(branch?.points_conversion_rate || "0.01"),
    );
  }, [
    branch?.max_points_to_redeem,
    branch?.points_conversion_rate,
    points,
    subtotal,
    tax,
    serviceFee,
    tipAmount,
    discountValue,
    deliveryFee,
  ]);

  const total = useMemo(() => {
    const deliveryFeeValue =
      deliveryFee.length > 0 ? parseFloat(deliveryFee) : 0;
    const pointsDeduction = usePoints
      ? pointsToRedeem * parseFloat(branch?.points_conversion_rate ?? "0.01")
      : 0;
    const discountDeduction = discountValue > 0 ? discountValue : 0;

    const calculatedTotal =
      subtotal +
      tax +
      serviceFee +
      tipAmount -
      discountDeduction -
      pointsDeduction +
      deliveryFeeValue;

    return calculatedTotal > 0 ? calculatedTotal : 0;
  }, [
    subtotal,
    tax,
    serviceFee,
    tipAmount,
    discountValue,
    usePoints,
    pointsToRedeem,
    branch?.points_conversion_rate,
    deliveryFee,
  ]);

  const orderItems = useMemo(() => {
    return adjustedCart.map((item) => ({
      product_id: item.id?.toString(),
      category_id: item.category,
      quantity: item.quantity,
      price_set: parseFloat(item.unit_price ?? "0.00") * item.quantity,
      discount_set: 0,
      branch_id: branchId,
      options: Object.entries(item.selected_options)
        .filter(([_, option]) => option.selected)
        .map(([_, option]) => ({
          name: option.name,
          quantity: option.quantity,
        })),
      ...(item.product_type === "Service" && {
        selected_appointment_date_time: item.selected_appointment_date_time,
        appointment_duration: item.appointment_duration,
        selected_service_provider: item.selected_service_provider,
      }),
    }));
  }, [adjustedCart, branchId]);

  return {
    cart,
    branchId,
    serviceFee,
    taxRate,
    adjustedCart,
    subtotal,
    tax,
    total,
    pointsToRedeem,
    orderItems,
    calculateItemTotal,
    productPrice,
  };
}
